import React, { useRef, useState } from 'react';
import config from '../config';
import { adminFetch } from '../utils/adminAuth';

const API_BASE = config.API_BASE;
const UPLOADS_BASE = config.UPLOADS_BASE || config.API_BASE;

export function getAdminImageUrl(img) {
  if (!img) return '';
  if (img.startsWith('http://') || img.startsWith('https://')) return img;
  if (img.startsWith('/uploads/')) return `${UPLOADS_BASE}${img}`;
  return `${UPLOADS_BASE}/uploads/${img.replace(/^\/+/, '')}`;
}

export async function parseAdminJsonResponse(res) {
  const text = await res.text();
  if (text.trimStart().startsWith('<')) {
    throw new Error(
      `Server returned a web page instead of JSON (HTTP ${res.status}). Check API URL configuration.`
    );
  }
  if (!text) {
    throw new Error(`Empty response from server (HTTP ${res.status})`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response (HTTP ${res.status})`);
  }
}

export async function fetchAdminJson(url, options = {}) {
  const res = await adminFetch(url, options);
  return parseAdminJsonResponse(res);
}

export function isImageFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  // Must match backend multer allow-list (HEIC/AVIF/BMP are rejected server-side)
  if (['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(type)) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(name);
}

export function describeRejectedImage(file) {
  const name = file?.name || 'file';
  const type = String(file?.type || '').toLowerCase();
  if (type.includes('heic') || type.includes('heif') || /\.heic$|\.heif$/i.test(name)) {
    return `${name}: HEIC is not supported — convert to JPG/PNG first`;
  }
  if (type.includes('avif') || /\.avif$/i.test(name)) {
    return `${name}: AVIF is not supported — convert to JPG/PNG first`;
  }
  return `${name}: use JPEG, PNG, GIF, or WebP`;
}

export async function uploadAdminImage(file) {
  // Prefer API_BASE; fall back to UPLOADS_BASE only if different
  const uploadBases = [...new Set([API_BASE, UPLOADS_BASE].filter(Boolean))];

  let lastError;
  for (const base of uploadBases) {
    try {
      // Recreate FormData each attempt — request body can only be read once
      const formData = new FormData();
      formData.append('image', file);
      const res = await adminFetch(`${base}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await parseAdminJsonResponse(res);
      if (data.success && data.url) return data.url;
      throw new Error(data.error || 'Upload failed');
    } catch (err) {
      lastError = err;
      const canRetry =
        uploadBases.length > 1 &&
        base !== uploadBases[uploadBases.length - 1] &&
        (err.message.includes('web page instead of JSON') ||
          err.message.includes('Empty response') ||
          err.message.includes('Failed to fetch') ||
          err.message.includes('NetworkError') ||
          err.message.includes('No token provided') ||
          err.message.includes('Invalid or expired token') ||
          err.message.includes('No file uploaded'));
      if (!canRetry) break;
    }
  }

  throw lastError || new Error('Upload failed');
}

export function dataTransferHasFiles(dataTransfer) {
  if (!dataTransfer) return false;
  if (dataTransfer.files && dataTransfer.files.length > 0) return true;
  const types = dataTransfer.types;
  if (!types) return false;
  if (typeof types.includes === 'function') {
    return types.includes('Files') || types.includes('files');
  }
  return Array.from(types).some((t) => String(t).toLowerCase() === 'files');
}

export default function AdminImageCarousel({
  images,
  onRemove,
  onSetMain,
  onDragStart,
  onDropReorder,
  onFilesDrop
}) {
  // Pointer-based reorder (HTML5 DnD fights file-drop handlers on the parent zone)
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const draggingIdxRef = useRef(null);
  const overIdxRef = useRef(null);

  const handleImageError = (e) => {
    e.target.src =
      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="76" height="76"%3E%3Crect fill="%23ddd" width="76" height="76"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="10"%3EImage%3C/text%3E%3C/svg%3E';
  };

  const allowFileDrag = (e) => {
    if (draggingIdxRef.current !== null) return false;
    if (!dataTransferHasFiles(e.dataTransfer)) return false;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    return true;
  };

  const handleFileDrop = (e) => {
    if (draggingIdxRef.current !== null) return false;
    if (!dataTransferHasFiles(e.dataTransfer)) return false;
    e.preventDefault();
    e.stopPropagation();
    if (onFilesDrop && e.dataTransfer?.files?.length) {
      onFilesDrop(e.dataTransfer.files);
    }
    return true;
  };

  const endPointerReorder = () => {
    const from = draggingIdxRef.current;
    const to = overIdxRef.current;
    draggingIdxRef.current = null;
    overIdxRef.current = null;
    setDraggingIdx(null);
    setOverIdx(null);
    if (from === null || to === null || from === to) return;
    onDropReorder(to);
  };

  const onThumbPointerDown = (idx, e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button')) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingIdxRef.current = idx;
    overIdxRef.current = idx;
    setDraggingIdx(idx);
    setOverIdx(idx);
    onDragStart?.(idx);
  };

  const onThumbPointerMove = (e) => {
    if (draggingIdxRef.current === null) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const thumb = el?.closest?.('[data-admin-image-idx]');
    if (!thumb) return;
    const idx = Number(thumb.getAttribute('data-admin-image-idx'));
    if (!Number.isFinite(idx) || idx === overIdxRef.current) return;
    overIdxRef.current = idx;
    setOverIdx(idx);
  };

  const onThumbPointerUp = (e) => {
    if (draggingIdxRef.current === null) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    endPointerReorder();
  };

  const onThumbPointerCancel = () => {
    draggingIdxRef.current = null;
    overIdxRef.current = null;
    setDraggingIdx(null);
    setOverIdx(null);
  };

  if (!images.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 10,
        justifyContent: 'center',
        touchAction: 'none'
      }}
      onDragEnter={allowFileDrag}
      onDragOver={(e) => {
        if (allowFileDrag(e)) return;
        e.preventDefault();
      }}
      onDrop={(e) => {
        if (handleFileDrop(e)) return;
      }}
    >
      {images.map((img, realIdx) => {
        const isDragging = draggingIdx === realIdx;
        const isOver = overIdx === realIdx && draggingIdx !== null && draggingIdx !== realIdx;
        return (
          <div
            key={`img-${realIdx}-${img}`}
            data-admin-image-idx={realIdx}
            onPointerDown={(e) => onThumbPointerDown(realIdx, e)}
            onPointerMove={onThumbPointerMove}
            onPointerUp={onThumbPointerUp}
            onPointerCancel={onThumbPointerCancel}
            style={{
              position: 'relative',
              display: 'inline-block',
              border: isOver ? '2px solid #2563eb' : realIdx === 0 ? '2px solid #000' : '1px solid #ccc',
              borderRadius: 4,
              padding: 2,
              background: '#fff',
              width: 80,
              height: 80,
              boxSizing: 'border-box',
              cursor: isDragging ? 'grabbing' : 'grab',
              opacity: isDragging ? 0.45 : 1,
              transform: isOver ? 'scale(1.06)' : 'none',
              transition: 'transform 0.12s ease, opacity 0.12s ease',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <img
              src={getAdminImageUrl(img)}
              alt=""
              draggable={false}
              onError={handleImageError}
              style={{
                width: 76,
                height: 76,
                objectFit: 'cover',
                borderRadius: 4,
                display: 'block',
                pointerEvents: 'none'
              }}
            />
            <button
              type="button"
              onClick={() => onRemove(realIdx)}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                background: '#fff',
                color: '#b00',
                border: 'none',
                borderRadius: '50%',
                width: 22,
                height: 22,
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 16,
                lineHeight: '18px',
                padding: 0
              }}
            >
              ×
            </button>
            {realIdx !== 0 && onSetMain && (
              <button
                type="button"
                onClick={() => onSetMain(realIdx)}
                style={{
                  position: 'absolute',
                  bottom: 2,
                  left: 2,
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  padding: '2px 6px',
                  cursor: 'pointer'
                }}
              >
                Set Main
              </button>
            )}
            {realIdx === 0 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 2,
                  left: 2,
                  background: '#000',
                  color: '#fff',
                  borderRadius: 4,
                  fontSize: 11,
                  padding: '2px 6px',
                  pointerEvents: 'none'
                }}
              >
                Main
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AdminImageDropzone({
  images,
  onImagesChange,
  onError,
  label = 'Images',
  mainLabel = 'Main',
  multiple = true,
  accept = 'image/*',
  showMainControls = true,
  helpText = ''
}) {
  const fileInputRef = useRef(null);
  const dragImageIdx = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleImageFiles = async (fileList) => {
    if (!fileList?.length) return;
    const allFiles = Array.from(fileList);
    const files = allFiles.filter(isImageFile);
    const rejected = allFiles.filter((f) => !isImageFile(f));
    if (!files.length) {
      onError?.(
        rejected.length
          ? `Could not upload: ${rejected.map(describeRejectedImage).join('; ')}`
          : 'Please drop or select JPEG, PNG, GIF, or WebP images'
      );
      return;
    }

    setUploading(true);
    onError?.('');
    const uploaded = [];
    const failures = [];
    try {
      for (const file of files) {
        try {
          const url = await uploadAdminImage(file);
          uploaded.push(url);
        } catch (err) {
          failures.push(`${file.name || 'image'}: ${err.message || 'Upload failed'}`);
        }
      }
      if (uploaded.length) {
        onImagesChange(multiple ? [...(images || []), ...uploaded] : uploaded.slice(0, 1));
      }
      if (failures.length) {
        onError?.(
          `Uploaded ${uploaded.length} of ${files.length}. Failed: ${failures.slice(0, 3).join('; ')}`
        );
      } else if (rejected.length) {
        onError?.(`Uploaded ${uploaded.length}. Skipped: ${rejected.map(describeRejectedImage).join('; ')}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      handleImageFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemove = (idx) => {
    onImagesChange((images || []).filter((_, i) => i !== idx));
  };

  const handleSetMain = (idx) => {
    const next = [...(images || [])];
    const [main] = next.splice(idx, 1);
    next.unshift(main);
    onImagesChange(next);
  };

  const handleDragStart = (idx) => {
    dragImageIdx.current = idx;
  };

  const handleDropReorder = (idx) => {
    if (dragImageIdx.current === null) return;
    const next = [...(images || [])];
    const [dragged] = next.splice(dragImageIdx.current, 1);
    next.splice(idx, 0, dragged);
    onImagesChange(next);
    dragImageIdx.current = null;
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>{label}</label>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        style={{
          marginTop: 8,
          padding: 16,
          border: '2px dashed #aaa',
          borderRadius: 6,
          textAlign: 'center',
          background: uploading ? '#f0f0f0' : '#fafafa',
          opacity: uploading ? 0.7 : 1
        }}
      >
        <span
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{ cursor: uploading ? 'wait' : 'pointer', display: 'block', marginBottom: 8 }}
        >
          {uploading
            ? 'Uploading…'
            : `Drag & drop images here (or click to select${multiple ? ' multiple' : ''})`}
        </span>
        {helpText ? (
          <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>{helpText}</p>
        ) : (
          mainLabel &&
          showMainControls &&
          images?.length > 0 && (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>
              First image is the {mainLabel.toLowerCase()}. Drag thumbnails to reorder.
            </p>
          )
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={async (e) => {
            if (!e.target.files?.length) return;
            await handleImageFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <AdminImageCarousel
          images={images || []}
          onRemove={handleRemove}
          onSetMain={showMainControls ? handleSetMain : undefined}
          onDragStart={handleDragStart}
          onDropReorder={handleDropReorder}
          onFilesDrop={handleImageFiles}
        />
      </div>
    </div>
  );
}
